import {
  BehaviorSubject,
  catchError,
  filter,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  timeout,
  TimeoutError,
} from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Dependencies } from '../../../../configurations/dependencies/dependencies.interface';
import { Action, AnyAction } from '../../../state/action.type';
import { AppState } from '../../../state/app.state';
import {
  retrieveSeasonFightsFailure,
  retrieveSeasonFightsSuccess,
} from '../../../state/seasonFights/seasonFights.action';
import {
  RetrieveSeasonFights,
  RETRIEVE_SEASON_FIGHTS,
} from '../../../state/seasonFights/seasonFights.action.type';
import { SEASON_FIGHTS_USECASE_ERROR_MESSAGES } from '../../errors/usecases.errorMessages/seasonFightsUsecase.error.messages';
import { createSeasonFightsFromFights } from '../../utils/seasonFights.utils';

export const retrieveSeasonFightsUsecase = (
  action$: Observable<Action>,
  state$: BehaviorSubject<AppState>,
  _dependencies: Dependencies,
): Observable<Action> =>
  action$.pipe(
    filter((action: AnyAction): action is RetrieveSeasonFights => {
      const isRetrieveSeasonFights = action.type === RETRIEVE_SEASON_FIGHTS;
      return isRetrieveSeasonFights;
    }),
    // Add deduplication to prevent rapid successive identical calls
    distinctUntilChanged(
      (prev: RetrieveSeasonFights, curr: RetrieveSeasonFights) => {
        const isSame = prev.payload.seasonId === curr.payload.seasonId;
        return isSame;
      },
    ),
    debounceTime(50), // Small debounce to handle rapid successive calls
    mergeMap((action: RetrieveSeasonFights) => {
      try {
        // Get fights from current state instead of making API call
        const currentState = state$.value;

        // If we already have fresh data for this exact season, return it immediately
        if (
          currentState.seasonFights.selectedSeasonId ===
            action.payload.seasonId &&
          currentState.seasonFights.seasonFights.length > 0 &&
          currentState.seasonFights.lastUpdated
        ) {
          console.log(
            'retrieveSeasonFightsUsecase: Already have fresh data for this season, returning existing data',
          );
          return of(
            retrieveSeasonFightsSuccess({
              seasonId: action.payload.seasonId,
              fights: currentState.seasonFights.seasonFights,
              lastUpdated: currentState.seasonFights.lastUpdated,
            }),
          );
        }

        const allFights = currentState.fights.fights;

        // Check if fights are loaded
        if (!allFights || allFights.length === 0) {
          console.log(
            'retrieveSeasonFightsUsecase: No fights data available, waiting for fights to load...',
          );

          // Instead of failing immediately, wait for fights data to become available with timeout
          return state$.pipe(
            filter((state) => state.fights.fights.length > 0), // Wait until fights are loaded
            take(1), // Only take the first emission when fights are available
            timeout(10000), // Wait maximum 10 seconds for fights data
            switchMap((state) => {
              console.log(
                'retrieveSeasonFightsUsecase: Fights data now available, proceeding...',
              );
              const fightsData = state.fights.fights;

              // Create seasonFights from the now-available fights data
              const seasonFights = createSeasonFightsFromFights(
                fightsData,
                action.payload.startDate,
                action.payload.endDate,
              );

              // Check if we found any fights for this season
              if (seasonFights.length === 0) {
                console.log(
                  'retrieveSeasonFightsUsecase: No fights found for season date range',
                );
                return of(
                  retrieveSeasonFightsFailure(
                    SEASON_FIGHTS_USECASE_ERROR_MESSAGES.RETRIEVAL_FAILED,
                  ),
                );
              }
              return of(
                retrieveSeasonFightsSuccess({
                  seasonId: action.payload.seasonId,
                  fights: seasonFights,
                  lastUpdated: Date.now(),
                }),
              );
            }),
            catchError((error) => {
              if (error instanceof TimeoutError) {
                console.error(
                  'retrieveSeasonFightsUsecase: Timeout waiting for fights data after 10 seconds',
                );
                return of(
                  retrieveSeasonFightsFailure(
                    'Timeout: Fights data not available after waiting 10 seconds. Please try refreshing the page.',
                  ),
                );
              }

              console.error(
                'retrieveSeasonFightsUsecase: Error while waiting for fights:',
                error,
              );
              return of(
                retrieveSeasonFightsFailure(
                  'Failed to retrieve season fights after waiting for data',
                ),
              );
            }),
          );
        }

        // Create seasonFights from existing fights based on date range
        const seasonFights = createSeasonFightsFromFights(
          allFights,
          action.payload.startDate,
          action.payload.endDate,
        );

        // Check if we found any fights for this season
        if (seasonFights.length === 0) {
          console.log(
            'retrieveSeasonFightsUsecase: No fights found for season date range',
          );
          return of(
            retrieveSeasonFightsFailure(
              SEASON_FIGHTS_USECASE_ERROR_MESSAGES.RETRIEVAL_FAILED,
            ),
          );
        }

        console.log(
          '✅ retrieveSeasonFightsUsecase: Success with',
          seasonFights.length,
          'fights',
        );
        return of(
          retrieveSeasonFightsSuccess({
            seasonId: action.payload.seasonId,
            fights: seasonFights,
            lastUpdated: Date.now(),
          }),
        );
      } catch (error) {
        console.error('retrieveSeasonFightsUsecase: Caught error:', error);
        return of(
          retrieveSeasonFightsFailure(
            `Failed to retrieve season fights: ${error instanceof Error ? error.message : 'Unknown error'}`,
          ),
        );
      }
    }),
    catchError((error: Error) => {
      console.error('retrieveSeasonFightsUsecase: Observable error:', error);
      return of(
        retrieveSeasonFightsFailure(
          `Unexpected error in season fights usecase: ${error.message}`,
        ),
      );
    }),
  );
